import type { CustomTabBarItem, CustomTabBarItemBadge } from './types'
import { reactive } from 'vue'

import { tabbarList as _tabbarList, customTabbarEnable, selectedTabbarStrategy, TABBAR_STRATEGY_MAP } from './config'

// TODO 1/2: 中间的鼓包tabbarItem的开关
const BULGE_ENABLE = false

/** tabbarList 里面的 path 从 pages.config.ts 得到 */
const tabbarList = reactive<CustomTabBarItem[]>(_tabbarList.map(item => ({
  ...item,
  pagePath: item.pagePath.startsWith('/') ? item.pagePath : `/${item.pagePath}`,
})))

if (customTabbarEnable && BULGE_ENABLE) {
  if (tabbarList.length % 2) {
    console.error('有鼓包时 tabbar 数量必须是偶数，否则样式很奇怪！！')
  }
  tabbarList.splice(tabbarList.length / 2, 0, {
    isBulge: true,
  } as CustomTabBarItem)
}

export function isPageTabbar(path: string) {
  if (selectedTabbarStrategy === TABBAR_STRATEGY_MAP.NO_TABBAR) {
    return false
  }
  const _path = path.split('?')[0]
  return tabbarList.some(item => item.pagePath === _path)
}

/**
 * 自定义 tabbar 的状态管理，原生 tabbar 无需关注本文件
 * tabbar 状态，增加 storageSync 保证刷新浏览器时在正确的 tabbar 页面
 * 使用reactive简单状态，而不是 pinia 全局状态
 */
const tabbarStore = reactive({
  curIdx: uni.getStorageSync('app-tabbar-index') || 0,
  prevIdx: uni.getStorageSync('app-tabbar-index') || 0,
  pagePath: uni.getStorageSync('app-tabbar-page') || '',
  setCurIdx(idx: number) {
    this.prevIdx = this.curIdx
    this.curIdx = idx
    uni.setStorageSync('app-tabbar-index', idx)
    // 同时更新页面路径
    if (tabbarList[idx] && tabbarList[idx].pagePath) {
      this.setPagePath(tabbarList[idx].pagePath)
    }
  },
  setPagePath(path: string) {
    this.pagePath = path
    uni.setStorageSync('app-tabbar-page', path)
    uni.setStorageSync('app-tabbar-timestamp', Date.now())
  },
  setTabbarItemBadge(idx: number, badge: CustomTabBarItemBadge) {
    if (tabbarList[idx]) {
      tabbarList[idx].badge = badge
    }
  },
  setAutoCurIdx(path: string) {
    // '/' 当做首页
    if (path === '/') {
      this.setCurIdx(0)
      return
    }
    const index = tabbarList.findIndex(item => item.pagePath === path)
    // console.log('tabbarList:', tabbarList)
    if (index === -1) {
      const pagesPathList = getCurrentPages().map(item => item.route.startsWith('/') ? item.route : `/${item.route}`)
      // console.log(pagesPathList)
      const flag = tabbarList.some(item => pagesPathList.includes(item.pagePath))
      if (!flag) {
        this.setCurIdx(0)
        return
      }
    }
    else {
      this.setCurIdx(index)
    }
  },
  restorePrevIdx() {
    if (this.prevIdx === this.curIdx)
      return
    this.setCurIdx(this.prevIdx)
    this.prevIdx = uni.getStorageSync('app-tabbar-index') || 0
  },
  /**
   * 获取当前页面路径
   */
  getCurrentPagePath(): string {
    const pages = getCurrentPages()
    if (pages.length === 0) {
      console.warn('[Tabbar] 页面栈为空')
      return ''
    }
    const currentPage = pages[pages.length - 1]
    const route = currentPage.route
    return route.startsWith('/') ? route : `/${route}`
  },
  /**
   * 根据页面路径获取对应的 tabbar 索引
   */
  getTabIndexByPath(path: string): number {
    const _path = path.split('?')[0]
    return tabbarList.findIndex(item => item.pagePath === _path)
  },
  /**
   * 验证索引是否有效
   */
  isValidTabIndex(idx: number): boolean {
    return idx >= 0 && idx < tabbarList.length && !tabbarList[idx].isBulge
  },
  /**
   * 验证页面同步状态
   * @returns true 表示一致，false 表示不一致
   */
  validatePageSync(): boolean {
    console.log('[Tabbar validatePageSync] 开始验证')
    
    const currentPath = this.getCurrentPagePath()
    console.log('[Tabbar validatePageSync] 当前页面路径:', currentPath)
    
    // 如果页面栈为空，返回不一致
    if (!currentPath) {
      console.warn('[Tabbar validatePageSync] 页面栈为空，需要同步')
      return false
    }
    
    // 如果当前页面不是 tabbar 页面，跳过验证
    const isTabbar = isPageTabbar(currentPath)
    console.log('[Tabbar validatePageSync] 是否为 tabbar 页面:', isTabbar)
    
    if (!isTabbar) {
      console.log('[Tabbar validatePageSync] 当前页面不是 tabbar 页面，跳过同步')
      return true
    }
    
    // 验证存储的索引是否有效
    const isValid = this.isValidTabIndex(this.curIdx)
    console.log('[Tabbar validatePageSync] 索引是否有效:', isValid, 'curIdx:', this.curIdx)
    
    if (!isValid) {
      console.warn('[Tabbar validatePageSync] 存储的索引无效:', this.curIdx)
      return false
    }
    
    // 获取当前页面对应的 tabbar 索引
    const expectedIdx = this.getTabIndexByPath(currentPath)
    console.log('[Tabbar validatePageSync] 期望的索引:', expectedIdx)
    
    // 如果当前页面不在 tabbar 列表中
    if (expectedIdx === -1) {
      console.warn('[Tabbar validatePageSync] 当前页面不在 tabbar 列表中:', currentPath)
      console.log('[Tabbar validatePageSync] tabbarList:', tabbarList.map(item => item.pagePath))
      return false
    }
    
    // 比较当前页面索引和存储的索引是否一致
    const isSync = expectedIdx === this.curIdx
    
    console.log('[Tabbar validatePageSync] 验证结果:', {
      currentPath,
      expectedIdx,
      storedIdx: this.curIdx,
      isSync,
    })
    
    if (!isSync) {
      console.warn('[Tabbar validatePageSync] 页面与 tabbar 状态不一致')
    }
    
    return isSync
  },
  /**
   * 同步页面到 tabbar
   */
  async syncPageToTab(): Promise<void> {
    const currentPath = this.getCurrentPagePath()
    
    // 如果页面栈为空，导航到默认首页
    if (!currentPath) {
      console.warn('[Tabbar] 页面栈为空，导航到默认首页')
      try {
        await uni.reLaunch({ url: tabbarList[0].pagePath })
        this.setCurIdx(0)
      } catch (error) {
        console.error('[Tabbar] 导航到默认首页失败:', error)
      }
      return
    }
    
    // 如果当前页面不是 tabbar 页面，不需要同步
    if (!isPageTabbar(currentPath)) {
      console.log('[Tabbar] 当前页面不是 tabbar 页面，无需同步')
      return
    }
    
    // 如果存储的索引无效，重置为默认首页
    if (!this.isValidTabIndex(this.curIdx)) {
      console.warn('[Tabbar] 索引无效，重置为默认首页')
      try {
        await uni.switchTab({ url: tabbarList[0].pagePath })
        this.setCurIdx(0)
      } catch (error) {
        console.error('[Tabbar] switchTab 失败，尝试 reLaunch:', error)
        try {
          await uni.reLaunch({ url: tabbarList[0].pagePath })
          this.setCurIdx(0)
        } catch (relaunchError) {
          console.error('[Tabbar] reLaunch 也失败:', relaunchError)
        }
      }
      return
    }
    
    // 获取应该显示的页面路径
    const targetPath = tabbarList[this.curIdx].pagePath
    
    // 如果当前页面与目标页面不一致，进行导航
    if (currentPath !== targetPath) {
      console.log('[Tabbar] 同步页面:', { from: currentPath, to: targetPath })
      try {
        await uni.switchTab({ url: targetPath })
      } catch (error) {
        console.error('[Tabbar] switchTab 失败，尝试 reLaunch:', error)
        try {
          await uni.reLaunch({ url: targetPath })
        } catch (relaunchError) {
          console.error('[Tabbar] reLaunch 也失败:', relaunchError)
          // 如果导航失败，更新 tabbar 状态以匹配当前页面
          const currentIdx = this.getTabIndexByPath(currentPath)
          if (currentIdx !== -1) {
            this.setCurIdx(currentIdx)
          }
        }
      }
    }
  },
  /**
   * 重置 tabbar 状态
   */
  resetTabbarState() {
    console.log('[Tabbar] 重置状态')
    this.setCurIdx(0)
    this.prevIdx = 0
    this.setPagePath(tabbarList[0].pagePath)
    uni.removeStorageSync('app-tabbar-index')
    uni.removeStorageSync('app-tabbar-page')
    uni.removeStorageSync('app-tabbar-timestamp')
  },
})

export { tabbarList, tabbarStore }
